'use client'

import { Box, FormLabel, MenuItem, TextField } from "@mui/material"
import { useResellers } from '@crm-src/hooks/useResellers'
import { useResellerAccess } from '@crm-src/components/reseller'

interface ResellerSelectorProps {
  value?: string | null
  onValueChange: (resellerId: string | null) => void
  placeholder?: string
  required?: boolean
  label?: string
  showLabel?: boolean
}

export function ResellerSelector({
  value,
  onValueChange,
  placeholder = "Select a reseller...",
  required = false,
  label = "Reseller",
  showLabel = true
}: ResellerSelectorProps) {
  // Reseller wall last line: even if a caller forgets to gate, never render a
  // reseller picker for a viewer without crm:resellers. See components/reseller.tsx.
  const resellerAccess = useResellerAccess()
  const { resellers, isLoading } = useResellers()

  if (!resellerAccess) return null

  const sortedResellers = [...resellers].sort((a, b) => {
    // Sort by code numerically if both have codes
    if (a.code && b.code) {
      return parseInt(a.code) - parseInt(b.code)
    }
    // Resellers with codes come before those without
    if (a.code && !b.code) return -1
    if (!a.code && b.code) return 1
    // If neither has code, sort by name
    return a.name.localeCompare(b.name)
  })

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {showLabel && (
        <FormLabel htmlFor="reseller" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
          {label} {required && <Box component="span" sx={{ color: "error.main" }}>*</Box>}
        </FormLabel>
      )}
      <TextField
        id="reseller"
        select
        size="small"
        fullWidth
        value={value || "none"}
        onChange={(e) => onValueChange(e.target.value === "none" ? null : e.target.value)}
        disabled={isLoading}
        SelectProps={{
          displayEmpty: true,
          renderValue: (val) => {
            const selected = val as string
            if (selected && selected !== "none") {
              const selectedReseller = resellers.find(r => String(r.id) === String(selected))
              return selectedReseller ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
                  <Box component="span">{selectedReseller.name}</Box>
                  {selectedReseller.code && (
                    <Box component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 600, color: "secondary.main" }}>
                      {selectedReseller.code}
                    </Box>
                  )}
                </Box>
              ) : null
            }
            if (selected === "none") {
              return "None (Direct Sales)"
            }
            return <Box component="span" sx={{ color: "text.disabled" }}>{isLoading ? "Loading resellers..." : placeholder}</Box>
          },
        }}
      >
        <MenuItem value="none">
          <Box component="span" sx={{ color: "text.secondary" }}>None</Box>
        </MenuItem>
        {sortedResellers.map((reseller) => (
          <MenuItem key={reseller.id} value={String(reseller.id)}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 2 }}>
              <Box component="span" sx={{ color: "text.primary" }}>{reseller.name}</Box>
              {reseller.code && (
                <Box component="span" sx={{ ml: 2, fontSize: "0.875rem", fontWeight: 600, color: "secondary.main" }}>
                  {reseller.code}
                </Box>
              )}
            </Box>
          </MenuItem>
        ))}
      </TextField>
    </Box>
  )
}
